import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const Row = ({ ok, label, detail }) => (
  <div className="flex items-start gap-2">
    {ok ? (
      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
    ) : (
      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
    )}
    <div>
      <span className="font-medium">{label}</span>
      {detail && <span className="text-gray-500"> — {detail}</span>}
    </div>
  </div>
);

const CustomDomainStatus = ({ campaignId, savedDomain, hasUnsavedChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!campaignId || !savedDomain) {
      setStatus(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/admin/campaigns/${campaignId}/custom-domain/status`);
      setStatus(res.data);
    } catch (err) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId, savedDomain]);

  useEffect(() => {
    load();
  }, [load]);

  const provision = async () => {
    setProvisioning(true);
    setMessage(null);
    try {
      const res = await api.post(`/admin/campaigns/${campaignId}/custom-domain/provision`);
      setMessage({ type: 'success', text: res.data.message });
    } catch (err) {
      const data = err.response?.data;
      setMessage({ type: 'error', text: data?.message || data?.error || 'Provisioning failed' });
    } finally {
      setProvisioning(false);
      load();
    }
  };

  if (!campaignId || !savedDomain) return null;

  const dns = status?.dns;
  const live = status?.https?.live;

  return (
    <div className="rounded-lg border border-blue-200 bg-white p-4 text-xs text-gray-700 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-blue-900">
          Domain status: {status?.domain || savedDomain}
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Re-check
        </button>
      </div>

      {hasUnsavedChange && (
        <p className="text-amber-700">
          The domain field has unsaved changes. Save settings first; status below reflects the saved domain.
        </p>
      )}

      {status && dns?.valid && (
        <div className="space-y-1.5">
          <Row
            ok={dns.root.ok}
            label="A record (@)"
            detail={
              dns.root.ok
                ? `points to ${status.server_ip}`
                : dns.root.extra?.length && dns.root.found.includes(status.server_ip)
                  ? `delete the other A record(s) for @: ${dns.root.extra.join(', ')} (e.g. registrar parking IPs)`
                  : `expected ${status.server_ip}, found ${dns.root.found.join(', ') || 'nothing yet'}`
            }
          />
          <Row
            ok={dns.www.ok}
            label="www (optional)"
            detail={dns.www.ok ? 'configured' : 'not configured; www will not be covered by the certificate'}
          />
          <Row ok={!!live} label="HTTPS" detail={live ? 'site is live' : 'not live yet'} />
        </div>
      )}

      {message && (
        <p className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>{message.text}</p>
      )}

      {status?.provisioning_enabled === false ? (
        <p className="text-gray-500">Automatic setup is not enabled on this server.</p>
      ) : (
        <button
          type="button"
          onClick={provision}
          disabled={provisioning || hasUnsavedChange || !dns?.ready}
          className="px-4 py-2 bg-teal-700 text-white rounded-lg text-xs font-medium hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {provisioning ? 'Setting up (can take ~30s)…' : live ? 'Re-run setup' : 'Enable domain & HTTPS'}
        </button>
      )}
    </div>
  );
};

export default CustomDomainStatus;
